import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Contact } from '../api/client';

const statusColors: Record<string, string> = {
  lead: 'bg-blue-100 text-blue-800',
  contactado: 'bg-yellow-100 text-yellow-800',
  en_negociacion: 'bg-orange-100 text-orange-800',
  cliente: 'bg-green-100 text-green-800',
  perdido: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  lead: 'Lead',
  contactado: 'Contactado',
  en_negociacion: 'En Negociación',
  cliente: 'Cliente',
  perdido: 'Perdido',
};

const channelLabels: Record<string, string> = {
  email: '📧 Email',
  dm: '💬 DM',
  llamada: '📞 Llamada',
  referido: '👥 Referido',
};

export default function ContactList() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    search: '',
  });

  useEffect(() => {
    loadContacts();
  }, [filters]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await api.contacts.list(filters);
      setContacts(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`¿Eliminar contacto "${name}"?`)) return;
    
    try {
      await api.contacts.delete(id);
      setContacts(contacts.filter(c => c.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">👥 Contactos</h1>
        <Link
          to="/contacts/new"
          className="bg-amazon-orange text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition font-medium text-sm sm:text-base"
        >
          ➕ Nuevo Lead
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="🔍 Buscar por nombre, empresa..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="lead">Lead</option>
            <option value="contactado">Contactado</option>
            <option value="en_negociacion">En Negociación</option>
            <option value="cliente">Cliente</option>
            <option value="perdido">Perdido</option>
          </select>
          <select
            value={filters.channel}
            onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
          >
            <option value="">Todos los canales</option>
            <option value="email">Email</option>
            <option value="dm">DM</option>
            <option value="llamada">Llamada</option>
            <option value="referido">Referido</option>
          </select>
          <button
            onClick={() => setFilters({ status: '', channel: '', search: '' })}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}

      {/* Lista de contactos */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amazon-orange mx-auto"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No se encontraron contactos
          </div>
        ) : (
          <>
          <table className="w-full hidden md:table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link to={`/contacts/${contact.id}`} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amazon-orange rounded-full flex items-center justify-center text-white font-bold">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{contact.name}</div>
                        {contact.email && (
                          <div className="text-sm text-gray-500">{contact.email}</div>
                        )}
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {contact.company || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {contact.channel ? channelLabels[contact.channel] : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[contact.status]}`}>
                      {statusLabels[contact.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(contact.created_at).toLocaleDateString('es')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/contacts/${contact.id}`}
                      className="text-amazon-orange hover:underline text-sm mr-3"
                    >
                      Ver
                    </Link>
                    <button
                      onClick={() => handleDelete(contact.id, contact.name)}
                      className="text-red-500 hover:underline text-sm"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Lista en tarjetas (móvil) */}
          <div className="md:hidden divide-y divide-gray-200">
            {contacts.map((contact) => (
              <div key={contact.id} className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 bg-amazon-orange rounded-full flex items-center justify-center text-white font-bold">
                  {contact.name.charAt(0).toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    to={`/contacts/${contact.id}`}
                    className="font-medium text-gray-800 hover:text-amazon-orange break-words"
                  >
                    {contact.name}
                  </Link>
                  <p className="text-sm text-gray-500 truncate">
                    {contact.company || 'Sin empresa'}
                  </p>
                  {contact.email && (
                    <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[contact.status]}`}>
                      {statusLabels[contact.status]}
                    </span>
                    <span className="text-xs text-gray-500">
                      {contact.channel ? channelLabels[contact.channel] : ''}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(contact.created_at).toLocaleDateString('es')}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Link
                    to={`/contacts/${contact.id}`}
                    className="text-amazon-orange hover:underline text-sm"
                  >
                    Ver
                  </Link>
                  <button
                    onClick={() => handleDelete(contact.id, contact.name)}
                    className="text-red-500 hover:underline text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      <div className="text-sm text-gray-500">
        Total: {contacts.length} contactos
      </div>
    </div>
  );
}
